import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ClipboardService } from 'ngx-clipboard';
import { ProxyService } from '../../services/proxy/proxy.service';
import { UserService } from '../../services/user/user.service';
import { Global } from '../../classes/global';

@Component({
  selector: 'app-proxy-list',
  templateUrl: './proxy-list.component.html',
  styleUrls: ['./proxy-list.component.scss']
})
export class ProxyListComponent implements OnInit {
  userData: any;
  @ViewChild('importModal') importModal: ElementRef;
  @ViewChild('addUsageModal') addUsageModal: ElementRef;
  @ViewChild('exportModal') exportModal: ElementRef;
  @ViewChild('editProxyModal') editProxyModal: ElementRef;
  @ViewChild('extendModal') extendModal: ElementRef;

  proxies: any[] = [];
  total = 0;
  query: any = {
    queryString: '',
    sourceId: null,
    status: null,
    protocol: null,
    availableForMerchantType: null,
    sortBy: null,
    sortType: null,
    pageSize: 100,
    pageNumber: 1
  };

  sources: any[] = [];
  merchants: any[] = [];
  selectedIds = new Set<string>();

  // Add usage
  addUsageProxyId = '';
  addUsageMerchantId: string = null;
  addUsageNote = '';
  addUsageIsActive = true;

  // Export
  exportText = '';

  // Edit proxy
  editingProxy: any = {};
  editProtocolHttp = false;
  editProtocolSocks5 = false;

  // Extend (Gia hạn)
  extendDays: number = null;
  extendCostUsd: number = null;
  extendLines = '';
  extendDayOptions = [
    { value: 30, label: '30 ngày' },
    { value: 60, label: '60 ngày' },
    { value: 90, label: '90 ngày' }
  ];

  statusOptions = [
    { id: null, name: 'Tất cả' },
    { id: 0, name: 'Active' },
    { id: 1, name: 'Inactive' },
    { id: 2, name: 'Expired' }
  ];

  protocolOptions = [
    { id: null, name: 'Tất cả' },
    { id: 'HTTP', name: 'HTTP' },
    { id: 'SOCKS5', name: 'SOCKS5' }
  ];

  merchantTypeOptions = [
    { id: null, name: 'Tất cả' },
    { id: 'Amazon', name: 'Có thể dùng cho Amazon' },
    { id: 'TikTok', name: 'Có thể dùng cho TikTok' },
    { id: 'Etsy', name: 'Có thể dùng cho Etsy' },
    { id: 'Ebay', name: 'Có thể dùng cho Ebay' },
    { id: 'Depop', name: 'Có thể dùng cho Depop' }
  ];

  constructor(
    private proxyService: ProxyService,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private clipboardService: ClipboardService,
    private userService: UserService,
    public global: Global
  ) {
    this.userData = userService.getLocalUserInfo();
  }

  get canDelete(): boolean {
    return this.userData && (this.userData.isSuperAdmin || this.userData.isAdmin);
  }

  ngOnInit() {
    this.loadSources();
    this.loadMerchants();
    this.loadProxies();
  }

  loadSources() {
    this.proxyService.querySources({ pageSize: 200, pageNumber: 1 }).subscribe(
      (res: any) => { this.sources = [{ id: null, name: 'Tất cả' }, ...(res.items || [])]; }
    );
  }

  loadMerchants() {
    this.proxyService.getMerchants().subscribe(
      (res: any) => { this.merchants = res.items || res || []; }
    );
  }

  loadProxies() {
    this.global.pageLoading = true;
    this.proxyService.queryProxies(this.query).subscribe(
      (res: any) => {
        this.proxies = (res.items || []).map((p: any) => ({
          ...p,
          autoRenew: p.autoRenew !== false,
          costPerMonth: p.costPerMonth ?? 0,
          proxyUsages: (p.proxyUsages || []).filter((u: any) => !u.isDeleted)
        }));
        this.total = res.total || 0;
        this.global.pageLoading = false;
      },
      () => { this.global.pageLoading = false; }
    );
  }

  /** Payload for SaveProxy — keeps grid + modal in sync. */
  private buildSaveProxyPayload(proxy: any, overrides: Record<string, any> = {}) {
    return {
      id: proxy.id,
      host: proxy.host,
      socks5Port: proxy.socks5Port,
      httpsPort: proxy.httpsPort,
      username: proxy.username,
      password: proxy.password,
      country: proxy.country,
      status: proxy.status,
      note: proxy.note,
      proxySourceId: proxy.proxySourceId,
      totalCost: proxy.totalCost ?? 0,
      costPerMonth: proxy.costPerMonth ?? 0,
      autoRenew: proxy.autoRenew !== false,
      startDate: proxy.startDate || null,
      endDate: proxy.endDate || null,
      ...overrides
    };
  }

  search() {
    this.query.pageNumber = 1;
    this.loadProxies();
  }

  clearSearch() {
    this.query.queryString = '';
    this.query.sourceId = null;
    this.query.status = null;
    this.query.protocol = null;
    this.query.availableForMerchantType = null;
    this.query.pageNumber = 1;
    this.loadProxies();
  }

  onPageChange(page: number) {
    this.query.pageNumber = page;
    this.loadProxies();
  }

  // ── Selection ──────────────────────────────────────────

  toggleSelect(id: string) {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  toggleSelectAll() {
    if (this.selectedIds.size === this.proxies.length) {
      this.selectedIds.clear();
    } else {
      this.proxies.forEach(p => this.selectedIds.add(p.id));
    }
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  get allSelected(): boolean {
    return this.proxies.length > 0 && this.selectedIds.size === this.proxies.length;
  }

  // ── Import ─────────────────────────────────────────────

  openImportModal() {
    this.modalService.open(this.importModal, { windowClass: 'lModal', size: 'lg' });
  }

  onImported() {
    this.loadProxies();
  }

  // ── Export ─────────────────────────────────────────────

  doExport() {
    const ids = Array.from(this.selectedIds);
    if (ids.length === 0) {
      this.toastr.warning('Chọn ít nhất một proxy');
      return;
    }
    this.global.pageLoading = true;
    this.proxyService.exportProxies(ids).subscribe(
      (res: any) => {
        this.exportText = res.text;
        this.global.pageLoading = false;
        this.modalService.open(this.exportModal, { windowClass: 'lModal' });
      },
      () => { this.global.pageLoading = false; }
    );
  }

  copyExport() {
    this.clipboardService.copy(this.exportText);
    this.toastr.success('Copied!');
  }

  // ── Delete proxy ───────────────────────────────────────

  deleteProxy(id: string) {
    if (!confirm('Xóa proxy này?')) return;
    this.global.pageLoading = true;
    this.proxyService.deleteProxy(id).subscribe(
      () => {
        this.toastr.success('Deleted');
        this.selectedIds.delete(id);
        this.loadProxies();
      },
      (err: any) => {
        this.toastr.error(err.error || 'Error');
        this.global.pageLoading = false;
      }
    );
  }

  // ── Edit proxy ──────────────────────────────────────────

  openEditModal(proxy: any) {
    this.editingProxy = {
      id: proxy.id,
      host: proxy.host,
      socks5Port: proxy.socks5Port,
      httpsPort: proxy.httpsPort,
      username: proxy.username,
      password: proxy.password,
      country: proxy.country,
      status: proxy.status,
      note: proxy.note,
      proxySourceId: proxy.proxySourceId,
      startDate: proxy.startDate ? proxy.startDate.substring(0, 10) : null,
      endDate: proxy.endDate ? proxy.endDate.substring(0, 10) : null,
      totalCost: proxy.totalCost || 0,
      costPerMonth: proxy.costPerMonth ?? 0,
      autoRenew: proxy.autoRenew !== false,
    };
    this.modalService.open(this.editProxyModal, { windowClass: 'lModal', size: 'lg' });
  }

  saveEditProxy() {
    if (!this.editingProxy.socks5Port && !this.editingProxy.httpsPort) {
      this.toastr.warning('Nhập ít nhất một port (SOCKS5 hoặc HTTPS)');
      return;
    }

    this.global.pageLoading = true;
    const req = {
      ...this.editingProxy,
      socks5Port: this.editingProxy.socks5Port ? parseInt(this.editingProxy.socks5Port, 10) : null,
      httpsPort: this.editingProxy.httpsPort ? parseInt(this.editingProxy.httpsPort, 10) : null,
      totalCost: parseFloat(this.editingProxy.totalCost as any) || 0,
      costPerMonth: parseFloat(this.editingProxy.costPerMonth as any) || 0,
      autoRenew: this.editingProxy.autoRenew !== false,
      startDate: this.editingProxy.startDate ? new Date(this.editingProxy.startDate).toISOString() : null,
      endDate: this.editingProxy.endDate ? new Date(this.editingProxy.endDate).toISOString() : null,
    };
    this.proxyService.saveProxy(req).subscribe(
      () => {
        this.toastr.success('Saved');
        this.modalService.dismissAll();
        this.loadProxies();
      },
      (err: any) => {
        this.toastr.error(err.error || 'Error');
        this.global.pageLoading = false;
      }
    );
  }

  // ── Extend (Gia hạn) ─────────────────────────────────────

  openExtendModal() {
    this.extendDays = null;
    this.extendCostUsd = null;
    this.extendLines = '';
    this.modalService.open(this.extendModal, { windowClass: 'lModal', size: 'lg' });
  }

  doExtend() {
    if (!this.extendDays) {
      this.toastr.warning('Chọn số ngày gia hạn');
      return;
    }
    const costUsd = parseFloat(this.extendCostUsd as any) || 0;
    if (costUsd <= 0) {
      this.toastr.warning('Nhập số tiền');
      return;
    }

    // If no proxies selected, require textarea lines
    if (this.selectedIds.size === 0 && !this.extendLines.trim()) {
      this.toastr.warning('Chọn proxy hoặc nhập danh sách proxy');
      return;
    }

    this.global.pageLoading = true;
    this.proxyService.extendProxies({
      ids: Array.from(this.selectedIds),
      lines: this.selectedIds.size === 0 ? this.extendLines : '',
      days: this.extendDays,
      costUsd: costUsd
    }).subscribe(
      (res: any) => {
        this.toastr.success(`Đã gia hạn ${res.extendedCount} proxy`);
        if (res.notFound && res.notFound.length > 0) {
          this.toastr.warning(`Không tìm thấy ${res.notFound.length} proxy: ${res.notFound.join(', ')}`);
        }
        this.modalService.dismissAll();
        this.loadProxies();
      },
      (err: any) => {
        this.toastr.error(err.error || 'Error');
        this.global.pageLoading = false;
      }
    );
  }

  // ── Inline editing ─────────────────────────────────────

  inlineSaveDate(proxy: any, field: string, event: any) {
    const value = event.target.value;
    const req = this.buildSaveProxyPayload(proxy, {
      startDate: field === 'startDate' ? (value ? new Date(value).toISOString() : null) : (proxy.startDate || null),
      endDate: field === 'endDate' ? (value ? new Date(value).toISOString() : null) : (proxy.endDate || null),
    });
    this.proxyService.saveProxy(req).subscribe(
      (res: any) => {
        proxy[field] = res[field];
        this.toastr.success('Saved');
      },
      (err: any) => { this.toastr.error(err.error || 'Error'); }
    );
  }

  inlineSaveNote(proxy: any, event: any) {
    const newNote = event.target.value;
    if (newNote === proxy.note) return;
    const req = this.buildSaveProxyPayload(proxy, { note: newNote });
    this.proxyService.saveProxy(req).subscribe(
      () => {
        proxy.note = newNote;
        this.toastr.success('Saved');
      },
      (err: any) => { this.toastr.error(err.error || 'Error'); }
    );
  }

  toggleAutoRenew(proxy: any, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    proxy.autoRenew = checked;
    const req = this.buildSaveProxyPayload(proxy, { autoRenew: checked });
    this.proxyService.saveProxy(req).subscribe(
      (res: any) => {
        proxy.autoRenew = res.autoRenew !== false;
        this.toastr.success('Saved');
      },
      (err: any) => {
        proxy.autoRenew = !checked;
        this.toastr.error(err.error || 'Error');
      }
    );
  }

  inlineSaveCostPerMonth(proxy: any, event: any) {
    const raw = parseFloat(event.target.value);
    const v = Number.isFinite(raw) ? raw : 0;
    if (v === (proxy.costPerMonth ?? 0)) return;
    proxy.costPerMonth = v;
    const req = this.buildSaveProxyPayload(proxy, { costPerMonth: v });
    this.proxyService.saveProxy(req).subscribe(
      (res: any) => {
        proxy.costPerMonth = res.costPerMonth ?? 0;
        this.toastr.success('Saved');
      },
      (err: any) => { this.toastr.error(err.error || 'Error'); }
    );
  }

  // ── Usage ──────────────────────────────────────────────

  openAddUsageModal(proxyId: string) {
    this.addUsageProxyId = proxyId;
    this.addUsageMerchantId = null;
    this.addUsageNote = '';
    this.addUsageIsActive = true;
    this.modalService.open(this.addUsageModal, { windowClass: 'lModal' });
  }

  saveUsage() {
    this.global.pageLoading = true;
    this.proxyService.addUsage({
      proxyId: this.addUsageProxyId,
      merchantId: this.addUsageMerchantId,
      note: this.addUsageNote,
      isActive: this.addUsageIsActive
    }).subscribe(
      () => {
        this.toastr.success('Usage added');
        this.modalService.dismissAll();
        this.loadProxies();
      },
      (err: any) => {
        this.toastr.error(err.error || 'Error');
        this.global.pageLoading = false;
      }
    );
  }

  toggleUsageActive(usage: any) {
    this.proxyService.updateUsage({
      id: usage.id,
      merchantId: usage.merchantId,
      note: usage.note,
      isActive: !usage.isActive
    }).subscribe(
      () => {
        usage.isActive = !usage.isActive;
      },
      (err: any) => { this.toastr.error(err.error || 'Error'); }
    );
  }

  deleteUsage(usageId: string) {
    this.proxyService.deleteUsage(usageId).subscribe(
      () => {
        this.toastr.success('Removed');
        this.loadProxies();
      },
      (err: any) => { this.toastr.error(err.error || 'Error'); }
    );
  }

  // ── Helpers ────────────────────────────────────────────

  getStatusLabel(status: number): string {
    switch (status) {
      case 0: return 'Active';
      case 1: return 'Inactive';
      case 2: return 'Expired';
      default: return '';
    }
  }

  getStatusClass(status: number): string {
    switch (status) {
      case 0: return 'badge-success';
      case 1: return 'badge-secondary';
      case 2: return 'badge-danger';
      default: return '';
    }
  }

  getUsageLabel(usage: any): string {
    if (usage.merchant) {
      const merchantName = usage.merchant.name || usage.merchant.merchantId || 'Merchant';
      const parts: string[] = [];

      // Team name
      if (usage.merchant.merchantGroup && usage.merchant.merchantGroup.name) {
        parts.push('[' + usage.merchant.merchantGroup.name + ']');
      }

      parts.push(merchantName);

      // Merchant type
      const typeMap: any = { 0: '', 1: 'Amazon', 2: 'TikTok', 3: 'Ebay', 4: 'Etsy', 5: 'Depop', 6: 'TikTokUk', 7: 'TikTokDe' };
      const typeName = typeMap[usage.merchant.merchantType];
      if (typeName) {
        parts.push('(' + typeName + ')');
      }

      return parts.join(' ');
    }
    return usage.note || 'Other';
  }

  getMerchantLabel(m: any): string {
    return m.name ? `${m.name} (${m.merchantId})` : m.merchantId;
  }
}
